// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Sort accidentals by +/- half-step changes
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import ListSorter from './tableListSorter.ts';
import CheckMark from './checkMark.ts';
import { toHTML } from '../uTheory.Font/font.ts';

class RhythmSorter extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.settings = {
      instructions: true,
      letters: { checkButton: true, letterButtons: false },
      progressTracker: true,
      checkMark: {},
    };

    this.layout = {
      letters: { percent: 10 },
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    this.options = options;
    this.compound = this.optionsTrue('compound');
    this.skill = this.compound ? 'dotted_rhythm_math' : 'basic_rhythm_math';
    this.listLength = this.optionSet('listLength') ? this.options.listLength : false;

    // now call the prototype's init function:
    super.init(displayDiv, options, this.settings).then(() => {
      this.progressTracker.start();
      this.letterPanel.addCheckCallback(this.checkAnswer, this);
      this.attempts = 0;
    });
  }

  displayInstructions() {
    this.instructionsDiv.innerHTML =
      'Re-arrange the rhythms, notes & rests to match their durations.';
  }

  createCustomDivs() {
    this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv);
    this.checkMark = new CheckMark(this.listDiv);
  }

  initializeCustomElements() {
    //      var bigFont = {fontSize: "5vw"};
    let rhythmTypes;
    let notes;
    let rests;
    let durations;

    if (!this.compound) {
      durations = [
        'Duration (in 4/4):',
        '4 beats',
        '2 beats',
        '1 beat',
        'half of a beat',
        'quarter of a beat',
        'eighth of a beat',
      ];
      rhythmTypes = ['Rhythm:', 'Whole', 'Half', 'Quarter', 'Eighth', 'Sixteenth', 'Thirty-Second'];
      notes = [
        { fitText: true, label: 'Note:' },
        { classes: 'rhythm', ariaLabel: 'whole-note', fitText: true, label: toHTML('rhythm', 'w') },
        { classes: 'rhythm', ariaLabel: 'half-note', fitText: true, label: toHTML('rhythm', 'h') },
        {
          classes: 'rhythm',
          ariaLabel: 'quarter-note',
          fitText: true,
          label: toHTML('rhythm', 'q'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'eighth-note',
          fitText: true,
          label: toHTML('rhythm', 'e'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'sixteenth-note',
          fitText: true,
          label: toHTML('rhythm', 's'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'thirty-second-note',
          fitText: true,
          label: toHTML('rhythm', '32'),
        },
      ];
      rests = [
        { fitText: true, label: 'Rest:' },
        {
          classes: 'rhythm',
          ariaLabel: 'whole-note rest',
          fitText: true,
          label: toHTML('rhythm', 'wr'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'half-note rest',
          fitText: true,
          label: toHTML('rhythm', 'hr'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'quarter-note rest',
          fitText: true,
          label: toHTML('rhythm', 'qr'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'eighth-note rest',
          fitText: true,
          label: toHTML('rhythm', 'er'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'sixteenth-note rest',
          fitText: true,
          label: toHTML('rhythm', 'sr'),
        },
        {
          classes: 'rhythm',
          ariaLabel: 'thirty-second-note rest',
          fitText: true,
          label: toHTML('rhythm', '32r'),
        },
      ];
    } else {
      durations = [
        'Duration in 12/8:',
        '4 beats',
        '2 beats',
        '1 beat',
        'two thirds of a beat',
        'third of a beat',
        'sixth of a beat',
      ];
      rhythmTypes = [
        'Rhythm:',
        'Dotted Whole',
        'Dotted Half',
        'Dotted Quarter',
        'Quarter',
        'Eighth',
        'Sixteenth',
      ];
      notes = [
        'Note:',
        { classes: 'rhythm', fitText: true, label: '2.' },
        { classes: 'rhythm', fitText: true, label: '3.' },
        { classes: 'rhythm', fitText: true, label: '4.' },
        { classes: 'rhythm', fitText: true, label: '4' },
        { classes: 'rhythm', fitText: true, label: '5' },
        { classes: 'rhythm', fitText: true, label: '6' },
      ];
      rests = [
        'Rest:',
        { classes: 'rhythm', fitText: true, label: '@>' },
        { classes: 'rhythm', fitText: true, label: '#>' },
        { classes: 'rhythm', fitText: true, label: '$>' },
        { classes: 'rhythm', fitText: true, label: '$' },
        { classes: 'rhythm', fitText: true, label: '%' },
        { classes: 'rhythm', fitText: true, label: '^' },
      ];
    }

    if (this.listLength) {
      const arrayLength = this.listLength + 1; // For headers
      durations.length = arrayLength;
      rhythmTypes.length = arrayLength;
      notes.length = arrayLength;
      rests.length = arrayLength;
    }

    const lists = [durations, rhythmTypes, notes, rests];
    this.listSorter = new ListSorter(this.listDiv, lists);
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  gameOver() {
    this.lettersDiv.style.display = 'none';
    this.listSorter.gameOver();
    super.gameOver();
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  checkAnswer(rightBoolean) {
    this.attempts++;
    let answers = this.listSorter.check();
    if (typeof rightBoolean !== 'undefined') {
      answers = rightBoolean ? { right: 10, wrong: 0 } : { wrong: 10, right: 0 };
    }
    const gameOver = answers.wrong === 0;
    this.progressTracker.multipleAnswers(
      [
        {
          skill: this.skill,
          right: answers.right,
          wrong: answers.wrong,
          questions: answers.right + answers.wrong,
        },
      ],
      answers.right + answers.wrong,
      gameOver
    );
    if (gameOver) {
      this.checkMark.check(2);
      this.gameOver();
    } else {
      this.checkMark.x();
      if (this.attempts > 1) this.listSorter.hint();
    }
  }

  debugRightAnswer() {
    this.checkAnswer(true);
  }

  debugWrongAnswer() {
    this.checkAnswer(false);
  }
}

RhythmSorter.registry = [
  {
    description: 'Simple Meter Rhythm Sort',
    skills: ['basic_rhythm_math'],
  },
  {
    description: 'Compound Meter Rhythm Sort',
    skills: ['dotted_rhythm_math'],
    options: { compound: true },
    testForSkillSetValidity: (skillIdsStringArray) => {
      // At least one available skill must have "compound" in it
      return skillIdsStringArray.some(id => id.includes('compound'));
    }
  },
];

export default RhythmSorter;
